import { useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconPaperclip, IconPencil, IconPlus, IconRefresh, IconTrash } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useSession } from '../../app/session';
import { EmptyState } from '../../components/EmptyState';
import { StatusBadge } from '../../components/StatusBadge';
import { formatDate } from '../../lib/format';
import { notifyError, notifySaved } from '../../lib/notify';
import {
  deleteDocument,
  listDocuments,
  revalidateRating,
  type DocumentRecord,
} from './documentApi';
import { DOCUMENT_KINDS, DOCUMENT_ORDER, type DocumentKind } from './documentKinds';
import { DocumentModal } from './DocumentModal';

export function QualificationsTab({ personId }: { personId: number }) {
  const [editing, setEditing] = useState<{
    kind: DocumentKind;
    record: DocumentRecord | null;
  } | null>(null);

  return (
    <Stack gap="lg">
      {DOCUMENT_ORDER.map((kind) => (
        <DocumentSection
          key={kind}
          kind={kind}
          personId={personId}
          onEdit={(record) => setEditing({ kind, record })}
        />
      ))}

      {editing ? (
        <DocumentModal
          opened
          onClose={() => setEditing(null)}
          kind={editing.kind}
          personId={personId}
          record={editing.record}
        />
      ) : null}
    </Stack>
  );
}

/**
 * One kind of paperwork as a card.
 *
 * Every row leads with the expiry traffic light, because on this screen the only question that
 * matters is whether the document is still good.
 */
function DocumentSection({
  kind,
  personId,
  onEdit,
}: {
  kind: DocumentKind;
  personId: number;
  onEdit: (record: DocumentRecord | null) => void;
}) {
  const { t } = useTranslation();
  const { can } = useSession();
  const queryClient = useQueryClient();
  const spec = DOCUMENT_KINDS[kind];
  const canEdit = can('staff.change');

  const documents = useQuery({
    queryKey: ['documents', kind, personId],
    queryFn: () => listDocuments(kind, personId),
  });

  const remove = useMutation({
    mutationFn: (id: number) => deleteDocument(kind, id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['documents', kind, personId] });
      await queryClient.invalidateQueries({ queryKey: ['person', personId] });
    },
    onError: notifyError,
  });

  const revalidate = useMutation({
    mutationFn: (id: number) => revalidateRating(id, {}),
    onSuccess: async () => {
      notifySaved(t('action.revalidate'));
      await queryClient.invalidateQueries({ queryKey: ['documents', kind, personId] });
      await queryClient.invalidateQueries({ queryKey: ['person', personId] });
    },
    onError: notifyError,
  });

  const rows = documents.data ?? [];

  return (
    <Card p="md">
      <Group justify="space-between" mb="sm">
        <Text fw={600}>{t(spec.titleKey)}</Text>
        {canEdit ? (
          <Button
            variant="light"
            size="compact-sm"
            leftSection={<IconPlus size={14} />}
            onClick={() => onEdit(null)}
          >
            {t(spec.addKey)}
          </Button>
        ) : null}
      </Group>

      {documents.isLoading ? (
        <Loader size="sm" />
      ) : rows.length === 0 ? (
        <EmptyState title={t(spec.emptyKey)} />
      ) : (
        <Table fz="sm" verticalSpacing={6} highlightOnHover>
          <Table.Tbody>
            {rows.map((row) => (
              <Table.Tr key={row.id}>
                <Table.Td w={110}>
                  <StatusBadge kind="validity" value={row.validity_status} size="sm" />
                </Table.Td>
                <Table.Td>
                  <Group gap="xs" wrap="nowrap">
                    <Text fz="sm" fw={600}>
                      {row.label}
                    </Text>
                    {row.code ? (
                      <Badge size="xs" variant="outline">
                        {row.code}
                      </Badge>
                    ) : null}
                    {row.allows_ctr_entry ? (
                      <Tooltip label={t('staff.ctrEntry')} withArrow>
                        <Badge size="xs" color="teal" variant="light">
                          CTR
                        </Badge>
                      </Tooltip>
                    ) : null}
                  </Group>
                  {row.reference || row.issuer ? (
                    <Text fz="xs" c="dimmed">
                      {[row.reference, row.issuer].filter(Boolean).join(' · ')}
                    </Text>
                  ) : null}
                </Table.Td>
                <Table.Td w={110} ff="monospace">
                  {formatDate(row.valid_until)}
                </Table.Td>
                <Table.Td w={90} ta="right">
                  {row.days_remaining !== null && row.days_remaining !== undefined ? (
                    <Text fz="xs" c={row.days_remaining < 0 ? 'red' : 'dimmed'}>
                      {t('common.daysRemaining')}: {row.days_remaining}
                    </Text>
                  ) : null}
                </Table.Td>
                <Table.Td w={130}>
                  <Group gap={4} justify="flex-end" wrap="nowrap">
                    {row.scan ? (
                      <Tooltip label={t('staff.scan')} withArrow>
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          component="a"
                          href={row.scan}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <IconPaperclip size={15} />
                        </ActionIcon>
                      </Tooltip>
                    ) : null}
                    {kind === 'rating' && canEdit ? (
                      <Tooltip label={t('action.revalidate')} withArrow>
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          loading={revalidate.isPending}
                          onClick={() => revalidate.mutate(row.id)}
                        >
                          <IconRefresh size={15} />
                        </ActionIcon>
                      </Tooltip>
                    ) : null}
                    {canEdit ? (
                      <>
                        <ActionIcon variant="subtle" size="sm" onClick={() => onEdit(row)}>
                          <IconPencil size={15} />
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          color="red"
                          loading={remove.isPending}
                          onClick={() => remove.mutate(row.id)}
                        >
                          <IconTrash size={15} />
                        </ActionIcon>
                      </>
                    ) : null}
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      {kind === 'rating' && rows.some((row) => row.revalidated_on) ? (
        <Text fz="xs" c="dimmed" mt="xs">
          {t('staff.revalidatedOn')}:{' '}
          {rows
            .filter((row) => row.revalidated_on)
            .map((row) => `${row.code ?? row.label} ${formatDate(row.revalidated_on)}`)
            .join(', ')}
        </Text>
      ) : null}
    </Card>
  );
}
